import { useMemo } from 'react';
import {
    CIRCULAR_TXN_KEY,
    DEFAULT_OPEN_GROUP,
    NET_KEY,
    OVERLAP_TAB_CONFIG,
    OpenGroup,
    RELATED_EMPTY_LABEL,
    RELATED_GROUP_LABEL,
    circularRevenuePct,
    formatWholeRupeesINR,
    isOverlappingRow,
    isSummaryRow,
    parseINR,
} from './components/overlap.constants';
import { SORT_DIRECTIONS, SortConfig } from '../../../../../utils/constants/commonConstants';

export interface RevenueTableData {
    fetchSuccess: boolean;
    headers: { name: string; key: string }[];
    rows: any[];
    totalRecords: number;
    records: number;
    // Per-month intra-company share (%), keyed identically to the month column keys.
    intraCompanyMonthly?: Record<string, number>;
    // Per-month intra-company revenue in ₹ (same keys) — used to compute Net B2B Revenue (Total − Intra).
    intraCompanyMonthlyAmount?: Record<string, number>;
    // Org-wide Σ circular ₹ across ALL customers (backend-computed) — the rows above only carry
    // the loaded page, so a client-side sum understates it for orgs beyond one page.
    circularTotalAmount?: number;
}

export interface RevenueTableView {
    displayHeaders: { name: string; key: string }[];
    displayRows: any[];
}

/**
 * Pure derivation of the B2B revenue table's display headers + rows from the raw response.
 * Extracted from the component and exported so the money math (Net B2B Revenue = Total − Intra,
 * the injected Circular/Net columns, and the Total-column sort) is unit-testable without React.
 */
export function buildRevenueTableView(
    tableData: RevenueTableData | undefined,
    isRevenue: boolean,
    sortConfig?: SortConfig,
    openGroup: OpenGroup = DEFAULT_OPEN_GROUP,
): RevenueTableView {
    const cfg = OVERLAP_TAB_CONFIG[isRevenue ? 'revenue' : 'expense'];

    // Both B2B tables get two extra pinned-right columns — Circular Txn. (% badge) and Net
    // (= Total − Circular) — injected client-side.
    const displayHeaders = tableData?.headers?.length
        ? [...tableData.headers, { name: 'Circular Txn.', key: CIRCULAR_TXN_KEY }, { name: 'Net', key: NET_KEY }]
        : tableData?.headers || [];

    const displayRows = (() => {
        let rows = tableData?.rows || [];
        if (!rows.length) return rows;

        // Related Parties = the loaded customers the backend labelled with a relation.
        const relatedChildren = rows.filter((r) => r?.relatedPartyRelation);

        // Relabel the backend total summary row (e.g. "Total Revenue") to its B2B label (e.g.
        // "Total B2B Revenue") and append a derived Net B2B row: Net = Total − Intra Company Txn.,
        // per month and total (per product — "Net B2B <x> after subtracting Intra Company Txn.").
        const totalRow = rows.find((r) => r?.name === cfg.totalRowName);
        rows = rows.map((r) => (r?.name === cfg.totalRowName ? { ...r, name: cfg.totalRowLabel } : r));

        const round1 = (n: number) => Math.round(n * 10) / 10;
        const totalNum = parseINR(totalRow?.[cfg.totalKey]);
        // Aggregate circular ₹ — feeds the summary rows' Circular/Net cells. The backend value is
        // org-wide and authoritative whenever present — including an explicit 0 (rows only carry
        // the loaded page); summing the page is a fallback for responses predating
        // `circularTotalAmount`.
        const totalCircular =
            tableData?.circularTotalAmount != null
                ? Number(tableData.circularTotalAmount) || 0
                : (tableData?.rows || []).reduce(
                      (sum, r) => sum + (!isSummaryRow(r) && isOverlappingRow(r) ? parseINR(r.circularValue) : 0),
                      0,
                  );
        const intraAmt: Record<string, number> = tableData?.intraCompanyMonthlyAmount || {};
        const totalIntra = Object.values(intraAmt).reduce((sum, v) => sum + (Number(v) || 0), 0);

        // Intra Company Txn. row — per-month intra share % (backend `intraCompanyMonthly`). Carries
        // the Total B2B row (per-month ₹) and the intra ₹ amounts so each badge can render a rich
        // Total / Intra / Net tooltip, mirroring Circular Txn. The summary columns follow the
        // GST-Enhancement-Phase-2 sheet: Total = Σintra/Total, Net = Σintra/(Total − Σcircular).
        const intra = tableData?.intraCompanyMonthly;
        const intraRow: any =
            intra && Object.keys(intra).length
                ? {
                      name: 'Intra Company Txn.',
                      __isIntra: true,
                      __totalRow: totalRow,
                      __amounts: { ...intraAmt, [cfg.totalKey]: totalIntra },
                      ...intra,
                  }
                : null;
        if (intraRow && totalNum > 0 && totalIntra > 0) {
            intraRow[cfg.totalKey] = round1((totalIntra / totalNum) * 100);
            if (totalNum - totalCircular > 0) {
                intraRow[NET_KEY] = round1((totalIntra / (totalNum - totalCircular)) * 100);
                // Base for the Net-column badge's tooltip: the circular-netted total.
                intraRow.__netBase = formatWholeRupeesINR(totalNum - totalCircular);
            }
        }

        if (totalRow) {
            // Total B2B row — aggregate Circular % (Σcircular/Total) and Net ₹ (Total − Σcircular).
            const totalB2bRow: any = rows.find((r) => r?.name === cfg.totalRowLabel);
            if (totalB2bRow) {
                if (totalNum > 0 && totalCircular > 0) {
                    totalB2bRow[CIRCULAR_TXN_KEY] = round1((totalCircular / totalNum) * 100);
                }
                totalB2bRow[NET_KEY] = formatWholeRupeesINR(totalNum - totalCircular);
            }

            const netB2bRow: any = {
                ...totalRow,
                name: cfg.netRowName,
                [cfg.totalKey]: formatWholeRupeesINR(totalNum - totalIntra),
                isOverlapping: undefined,
                circularValue: undefined,
                circularTotal: undefined,
                circularPct: undefined,
            };
            // Net per month = that month's Total − that month's intra amount. Months with no intra
            // keep the Total value (net = total).
            Object.keys(intraAmt).forEach((m) => {
                if (totalRow[m] !== undefined) {
                    netB2bRow[m] = formatWholeRupeesINR(parseINR(totalRow[m]) - (Number(intraAmt[m]) || 0));
                }
            });
            // Per the sheet: Circular = Σcircular/(Total − Σintra), Net = Total − Σintra − Σcircular.
            if (totalCircular > 0 && totalNum - totalIntra > 0) {
                netB2bRow[CIRCULAR_TXN_KEY] = round1((totalCircular / (totalNum - totalIntra)) * 100);
            }
            netB2bRow[NET_KEY] = formatWholeRupeesINR(totalNum - totalIntra - totalCircular);
            rows = [...rows, ...(intraRow ? [intraRow] : []), netB2bRow];
        } else if (intraRow) {
            rows = [...rows, intraRow];
        }

        // Sort children by the chosen column (Total, a month, Circular % or Net); group and summary
        // rows never move. Cells with no value ('-'/absent) sort as 0.
        const customers = rows.filter((r) => !isSummaryRow(r) && !r?.relatedPartyRelation);
        const summaries = rows.filter((r) => isSummaryRow(r));
        const field = sortConfig?.field || cfg.totalKey;
        const asc = sortConfig?.direction === SORT_DIRECTIONS.ASC;
        // Circular/Net are derived columns — mirror what their cells display.
        const sortValue = (r: any): number => {
            if (field === CIRCULAR_TXN_KEY) return isOverlappingRow(r) ? parseFloat(String(circularRevenuePct(r, cfg.totalKey))) || 0 : 0;
            if (field === NET_KEY) return parseINR(r?.[cfg.totalKey]) - (isOverlappingRow(r) ? parseINR(r?.circularValue) : 0);
            return parseINR(r?.[field]);
        };
        const sortChildren = (list: any[]) =>
            sortConfig?.direction
                ? [...list].sort((a, b) => {
                      const diff = sortValue(a) - sortValue(b);
                      return asc ? diff : -diff;
                  })
                : list;

        // Related Parties bar aggregates the loaded related rows; Circular % / Net use the Total B2B convention.
        const sumOf = (key: string) => relatedChildren.reduce((sum, r) => sum + parseINR(r?.[key]), 0);
        const relatedGroup: any = { name: RELATED_GROUP_LABEL, __group: 'related', __count: relatedChildren.length };
        displayHeaders.slice(1).forEach(({ key }) => {
            if (relatedChildren.some((r) => typeof r?.[key] === 'string' && r[key].includes('₹'))) {
                relatedGroup[key] = formatWholeRupeesINR(sumOf(key));
            }
        });
        const groupTotal = sumOf(cfg.totalKey);
        const groupCircular = relatedChildren.reduce((sum, r) => sum + (isOverlappingRow(r) ? parseINR(r.circularValue) : 0), 0);
        if (groupCircular > 0 && groupTotal > 0) relatedGroup[CIRCULAR_TXN_KEY] = round1((groupCircular / groupTotal) * 100);
        if (relatedChildren.length) relatedGroup[NET_KEY] = formatWholeRupeesINR(groupTotal - groupCircular);

        // B2B bar is org-wide (rows are paginated): Total B2B row minus the Related Parties bar and the Intra Company Txn.
        // amount, so B2B + Related Parties + Intra Company Txn. = Total. The badge excludes the related rows already loaded.
        const customersGroup: any = {
            name: cfg.customersGroupLabel,
            __group: 'customers',
            __count: Math.max(0, (tableData?.totalRecords ?? 0) - relatedChildren.length),
        };
        const totalB2bValues: any = rows.find((r) => r?.name === cfg.totalRowLabel);
        if (totalB2bValues) {
            displayHeaders.slice(1).forEach(({ key }) => {
                if (key === CIRCULAR_TXN_KEY || key === NET_KEY) return;
                const value = totalB2bValues[key];
                const intra = key === cfg.totalKey ? totalIntra : Number(intraAmt[key]) || 0;
                customersGroup[key] =
                    typeof value === 'string' && value.includes('₹') ? formatWholeRupeesINR(parseINR(value) - sumOf(key) - intra) : value;
            });
            const b2bTotal = totalNum - groupTotal - totalIntra;
            const b2bCircular = totalCircular - groupCircular;
            if (b2bCircular > 0 && b2bTotal > 0) customersGroup[CIRCULAR_TXN_KEY] = round1((b2bCircular / b2bTotal) * 100);
            customersGroup[NET_KEY] = formatWholeRupeesINR(b2bTotal - Math.max(0, b2bCircular));
        }

        return [
            customersGroup,
            ...(openGroup === 'customers' ? sortChildren(customers) : []),
            relatedGroup,
            ...(openGroup === 'related'
                ? relatedChildren.length
                    ? sortChildren(relatedChildren)
                    : [{ name: RELATED_EMPTY_LABEL, __emptyGroup: 'related' }]
                : []),
            ...summaries,
        ];
    })();

    return { displayHeaders, displayRows };
}

/** Memoised React wrapper around buildRevenueTableView. */
export default function useRevenueTableRows(
    tableData: RevenueTableData | undefined,
    isRevenue: boolean,
    sortConfig?: SortConfig,
    openGroup: OpenGroup = DEFAULT_OPEN_GROUP,
): RevenueTableView {
    return useMemo(() => buildRevenueTableView(tableData, isRevenue, sortConfig, openGroup), [tableData, isRevenue, sortConfig, openGroup]);
}
